/**
 * Dialog modale "Nuovo registro" .
 *
 * Flusso semplice in un solo step:
 *  1. L'unità locale è già selezionata in sidebar (operatore + sito).
 *  2. L'utente sceglie le attività + descrizione (opzionale).
 *  3. Due CTA finali: "Salva bozza" (locale) e "Apri su RENTRI".
 *
 * In modalità "edit bozza" precarica i campi dalla DraftItem fornita; in caso
 * di stato `sent_failed` mostra il messaggio d'errore RENTRI in alto.
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, PlusCircle, Save, Send, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ATTIVITA_REGISTRO,
  OPERAZIONI_D,
  OPERAZIONI_R,
} from "@/lib/rentriChoices";
import { endpoints, ApiError, type DraftItem } from "@/api/client";

type RegistroPayload = {
  num_iscr_sito: string;
  attivita: string[];
  attivita_rec_smalt?: string[];
  descrizione?: string;
};

export function NuovoRegistroDialog({
  open,
  onOpenChange,
  numIscrSito,
  numIscrSitoLabel,
  draft,
  onSaved,
  onSent,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  numIscrSito: string;
  numIscrSitoLabel?: string;
  draft?: DraftItem;
  onSaved?: () => void;
  onSent?: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = !!draft;

  const initial = (() => {
    const p = draft?.payload as Record<string, unknown> | undefined;
    return {
      attivita: Array.isArray(p?.attivita)
        ? (p?.attivita as string[]).slice()
        : ["Produzione"],
      operazioni: Array.isArray(p?.attivita_rec_smalt)
        ? (p?.attivita_rec_smalt as string[]).slice()
        : [],
      descrizione: typeof p?.descrizione === "string" ? p.descrizione : "",
    };
  })();

  const [attivita, setAttivita] = useState<string[]>(initial.attivita);
  const [operazioni, setOperazioni] = useState<string[]>(initial.operazioni);
  const [descrizione, setDescrizione] = useState<string>(initial.descrizione);
  const [validation, setValidation] =
    useState<{ valid: boolean; issues: { field: string; message: string; severity: string }[] } | null>(null);
  const [rentriError, setRentriError] = useState<string | null>(() =>
    draft?.status === "sent_failed" && draft.rentri_response
      ? rentriDetail(draft.rentri_response)
      : null,
  );

  useEffect(() => {
    if (!open) return;
    const p = draft?.payload as Record<string, unknown> | undefined;
    setAttivita(
      Array.isArray(p?.attivita)
        ? (p?.attivita as string[]).slice()
        : ["Produzione"],
    );
    setOperazioni(
      Array.isArray(p?.attivita_rec_smalt)
        ? (p?.attivita_rec_smalt as string[]).slice()
        : [],
    );
    setDescrizione(typeof p?.descrizione === "string" ? p.descrizione : "");
    setValidation(null);
    setRentriError(
      draft?.status === "sent_failed" && draft.rentri_response
        ? rentriDetail(draft.rentri_response)
        : null,
    );
  }, [open, draft?.id, draft?.updated_at, draft?.status]);

  const needsRecSmalt =
    attivita.includes("Recupero") || attivita.includes("Smaltimento");

  const buildPayload = (): RegistroPayload => {
    const p: RegistroPayload = {
      num_iscr_sito: numIscrSito,
      attivita: attivita.slice(),
    };
    if (needsRecSmalt && operazioni.length > 0) {
      p.attivita_rec_smalt = operazioni.slice();
    }
    const desc = descrizione.trim();
    if (desc) p.descrizione = desc;
    return p;
  };

  const validateMut = useMutation({
    mutationFn: () => endpoints.validate.registroApertura(buildPayload()),
    onSuccess: (r) => {
      setValidation({ valid: r.valid, issues: r.issues });
      if (!r.valid) {
        toast.error("Validazione locale fallita", {
          description: `${r.issues.length} problemi rilevati`,
        });
      } else {
        toast.success("Registro valido (validazione locale)");
      }
    },
  });

  const saveDraftMut = useMutation({
    mutationFn: async () => {
      if (isEdit && draft) {
        return endpoints.registri.aggiornaBozza(draft.id, {
          payload: buildPayload(),
          title: titleFor(buildPayload()),
        });
      }
      return endpoints.registri.creaBozza(buildPayload(), titleFor(buildPayload()));
    },
    onSuccess: () => {
      toast.success(isEdit ? "Bozza aggiornata" : "Bozza salvata");
      qc.invalidateQueries({ queryKey: ["registri"] });
      onSaved?.();
      onOpenChange(false);
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : "Errore salvataggio bozza";
      toast.error(msg);
    },
  });

  const sendMut = useMutation({
    mutationFn: async () => {
      const payload = buildPayload();
      if (isEdit && draft) {
        await endpoints.registri.aggiornaBozza(draft.id, {
          payload,
          title: titleFor(payload),
        });
        return endpoints.registri.inviaBozza(draft.id);
      }
      return endpoints.registri.creaEInvia(payload, titleFor(payload));
    },
    onSuccess: (res) => {
      const status = (res as { rentri_status?: number }).rentri_status ?? 0;
      const body = (res as { rentri_body?: unknown }).rentri_body;
      if (status >= 200 && status < 300) {
        toast.success("Registro aperto su RENTRI");
        qc.invalidateQueries({ queryKey: ["registri"] });
        setRentriError(null);
        onSent?.();
        onOpenChange(false);
      } else if (status === 202) {
        toast.success("Registro in elaborazione su RENTRI");
        qc.invalidateQueries({ queryKey: ["registri"] });
        onSent?.();
        onOpenChange(false);
      } else {
        const detail = rentriDetail(body);
        setRentriError(detail || `RENTRI HTTP ${status}`);
        toast.error("RENTRI ha rifiutato l'apertura", { description: detail });
        qc.invalidateQueries({ queryKey: ["registri"] });
      }
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) {
        const detail = rentriDetail(e.body) || e.message;
        setRentriError(detail);
        toast.error(`Apertura fallita (HTTP ${e.status})`, { description: detail });
      } else {
        const msg = e instanceof Error ? e.message : "Errore invio";
        setRentriError(msg);
        toast.error(msg);
      }
    },
  });

  const handleToggleAttivita = (v: string) =>
    setAttivita((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
    );
  const handleToggleOperazione = (v: string) =>
    setOperazioni((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
    );

  const canSave =
    !sendMut.isPending &&
    !saveDraftMut.isPending &&
    !!numIscrSito &&
    attivita.length > 0;

  const canSend = canSave && (!needsRecSmalt || operazioni.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PlusCircle className="h-5 w-5" />
            {isEdit ? "Modifica bozza registro" : "Nuovo registro"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Sito a registro:{" "}
            <span className="font-mono">{numIscrSito}</span>
            {numIscrSitoLabel ? <> — {numIscrSitoLabel}</> : null}
          </DialogDescription>
        </DialogHeader>

        {rentriError && (
          <div
            data-testid="registro-rentri-error"
            className="flex items-start gap-2 rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-sm text-rose-700 dark:text-rose-400"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div>
              <p className="font-medium">RENTRI ha rifiutato l'ultima apertura</p>
              <p className="text-xs opacity-90">{rentriError}</p>
            </div>
          </div>
        )}

        <div className="space-y-5">
          <div>
            <p className="text-xs font-medium text-muted-foreground">
              Attività del registro * (almeno 1, coerenti con l'iscrizione)
            </p>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {ATTIVITA_REGISTRO.map((a) => (
                <label
                  key={a.value}
                  className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm ${
                    attivita.includes(a.value)
                      ? "border-primary bg-primary/5"
                      : "border-input"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={attivita.includes(a.value)}
                    onChange={() => handleToggleAttivita(a.value)}
                  />
                  <span>{a.label}</span>
                </label>
              ))}
            </div>
          </div>

          {needsRecSmalt && (
            <div>
              <p className="text-xs font-medium text-muted-foreground">
                Operazioni R/D * (richiesto se hai Recupero o Smaltimento)
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {[...OPERAZIONI_R, ...OPERAZIONI_D].map((op) => {
                  const selected = operazioni.includes(op.value);
                  return (
                    <label
                      key={op.value}
                      title={op.description ?? op.label}
                      className={`cursor-pointer rounded-md border px-2 py-1 text-xs font-mono ${
                        selected
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-input"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={selected}
                        onChange={() => handleToggleOperazione(op.value)}
                      />
                      {op.value}
                    </label>
                  );
                })}
              </div>
              {operazioni.length > 0 && (
                <ul className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
                  {operazioni.map((code) => {
                    const all = [...OPERAZIONI_R, ...OPERAZIONI_D];
                    const opt = all.find((o) => o.value === code);
                    if (!opt?.description) return null;
                    return (
                      <li key={code}>
                        <span className="font-mono">{code}</span> — {opt.description}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Descrizione (opzionale, max 250 caratteri)
            </label>
            <Input
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              maxLength={250}
              placeholder="Es. Registro produzione cantiere via Roma 12"
            />
            <p className="mt-1 text-right text-[11px] text-muted-foreground">
              {descrizione.length}/250
            </p>
          </div>

          {validation && (
            <div
              className={`rounded-md border p-3 text-sm ${
                validation.valid
                  ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400"
                  : "border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-400"
              }`}
            >
              <p className="font-medium">
                {validation.valid ? "Registro valido" : "Registro non valido"}
              </p>
              {validation.issues.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-xs">
                  {validation.issues.map((i, idx) => (
                    <li key={idx}>
                      <Badge
                        variant={i.severity === "error" ? "destructive" : "secondary"}
                      >
                        {i.severity}
                      </Badge>{" "}
                      <span className="font-mono">{i.field}</span> — {i.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => validateMut.mutate()}
            disabled={validateMut.isPending || !numIscrSito}
            title="Verifica i dati offline senza inviarli a RENTRI"
          >
            <ShieldCheck className="mr-1.5 h-4 w-4" />
            {validateMut.isPending ? "Validazione…" : "Valida (locale)"}
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            variant="outline"
            onClick={() => saveDraftMut.mutate()}
            disabled={!canSave}
          >
            <Save className="mr-1.5 h-4 w-4" />
            {saveDraftMut.isPending ? "Salvataggio…" : "Salva bozza"}
          </Button>
          <Button onClick={() => sendMut.mutate()} disabled={!canSend}>
            <Send className="mr-1.5 h-4 w-4" />
            {sendMut.isPending
              ? "Invio…"
              : isEdit && draft?.status === "sent_failed"
                ? "Rinvia a RENTRI"
                : "Apri su RENTRI"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function titleFor(p: RegistroPayload): string {
  const parts: string[] = [];
  if (p.descrizione) parts.push(p.descrizione);
  if (p.attivita?.length) parts.push(`(${p.attivita.join("+")})`);
  return parts.join(" ").slice(0, 200) || `Registro ${p.num_iscr_sito}`;
}

function rentriDetail(body: unknown): string {
  if (!body || typeof body !== "object") {
    return typeof body === "string" ? body : "";
  }
  const o = body as Record<string, unknown>;
  const detail = o.detail ?? o.title ?? o.message ?? o.Detail ?? o.Title;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const dd = detail as Record<string, unknown>;
    for (const k of ["title", "detail", "message"]) {
      const v = dd[k];
      if (typeof v === "string") return v;
    }
  }
  const ms = o.model_state;
  if (ms && typeof ms === "object") {
    const entries = Object.entries(ms as Record<string, unknown>);
    if (entries.length > 0) {
      const [field, vals] = entries[0];
      if (Array.isArray(vals) && vals.length > 0) {
        return `${field}: ${String(vals[0])}`;
      }
    }
  }
  if (typeof o.raw === "string") return o.raw;
  return JSON.stringify(body).slice(0, 240);
}
